/**
 * CycleGrid — the Monitor view for a multi-leg option-strategy instance as ONE rich grid.
 *
 * One row per TRADE (cycle): symbol, structure (spread name + Credit/Debit + Long/Short +
 * strikes), expiry, lots, net entry, live/realised P&L, ROI on margin, max-profit / risk,
 * status + plain-English reason, and age. Click a row to expand its legs (entry / exit /
 * P&L) — so the per-leg detail is on demand, never repeated across the page.
 *
 * Replaces the old stack of one-mini-table-per-cycle (which repeated the same header for
 * every trade and scattered the data down the page).
 */
import React, { useState } from 'react';
import {
  Box, Chip, Table, TableHead, TableRow, TableCell, TableBody, Collapse, IconButton, Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Cycle, structureName, cycleLots, netEntryInr, netEntryPts, cyclePnlInr, cycleRoiPct, reasonLabel, durationLabel,
} from './cycleFormat';

const inr0 = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
const inrSigned = (v: number) => `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;
const HEADERS = ['', 'Symbol', 'Structure', 'Expiry', 'Lots', 'Entry', 'P&L', 'ROI', 'Max P / Risk', 'Status', 'Age'];

const KindChip: React.FC<{ kind?: string | null }> = ({ kind }) => {
  const k = (kind || '').toUpperCase();
  if (k !== 'CREDIT' && k !== 'DEBIT') return null;
  return <Chip size="small" label={k === 'CREDIT' ? 'Credit' : 'Debit'} sx={{ height: 16, fontSize: '0.54rem', fontWeight: 800, bgcolor: k === 'CREDIT' ? '#00897b' : '#3949ab', color: '#fff' }} />;
};
const DirChip: React.FC<{ side?: string | null }> = ({ side }) => {
  const s = (side || '').toUpperCase();
  if (s !== 'LONG' && s !== 'SHORT') return null;
  return <Chip size="small" label={s === 'LONG' ? 'Long' : 'Short'} sx={{ height: 16, fontSize: '0.54rem', fontWeight: 800, bgcolor: s === 'LONG' ? '#1b5e20' : '#7f1d1d', color: '#fff' }} />;
};

const CycleRow: React.FC<{ c: Cycle }> = ({ c }) => {
  const [open, setOpen] = useState(false);
  const legs = c.legs || [];
  const optType = legs[0]?.optType;
  const name = structureName(c.meta?.kind, c.meta?.avSide, optType);
  const strikes = c.meta?.shortStrike != null && c.meta?.longStrike != null
    ? `${Number(c.meta.shortStrike).toLocaleString('en-IN')} / ${Number(c.meta.longStrike).toLocaleString('en-IN')}`
    : legs.map(l => Number(l.strike).toLocaleString('en-IN')).join(' / ');
  const lots = cycleLots(c);
  const entryInr = netEntryInr(c);
  const entryPts = netEntryPts(c);
  const pnl = cyclePnlInr(c);
  const roi = cycleRoiPct(c);
  const isOpen = c.status === 'OPEN';
  const expiry = c.meta?.expiry || legs[0]?.expiry || '—';
  const pnlColor = pnl == null ? 'text.disabled' : pnl >= 0 ? 'success.main' : 'error.main';
  const cell = { fontSize: '0.7rem', px: 0.8, py: 0.35, whiteSpace: 'nowrap' as const };

  return (
    <>
      <TableRow hover onClick={() => setOpen(o => !o)} sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell sx={{ px: 0.3, py: 0 }}>
          <IconButton size="small" sx={{ p: 0.2, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }}><KeyboardArrowRightIcon sx={{ fontSize: 16 }} /></IconButton>
        </TableCell>
        <TableCell sx={{ ...cell, fontWeight: 700 }}>{c.symbol}</TableCell>
        <TableCell sx={cell}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>{name}</Typography>
            <KindChip kind={c.meta?.kind} />
            <DirChip side={c.meta?.avSide} />
            <Typography component="span" sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>{strikes}</Typography>
            {c.meta?.rr != null && c.meta.rr < 0.5 && (
              <Tooltip arrow title={`Thin edge — reward:risk ${Number(c.meta.rr).toFixed(2)} (max profit is small vs the risk). New entries below the min reward:risk are now skipped.`}>
                <Chip size="small" label={`⚠ R:R ${Number(c.meta.rr).toFixed(2)}`} sx={{ height: 16, fontSize: '0.52rem', fontWeight: 800, bgcolor: '#b26a00', color: '#fff' }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell sx={cell}>{expiry}</TableCell>
        <TableCell sx={cell}>{lots || '—'}</TableCell>
        <TableCell sx={cell}>
          {entryInr == null ? '—' : (
            <Tooltip arrow title={`${entryPts != null ? `${entryPts >= 0 ? '+' : ''}${entryPts.toFixed(2)} pts · ` : ''}${entryInr >= 0 ? 'credit received' : 'debit paid'}`}>
              <span style={{ fontWeight: 600 }}>{inrSigned(entryInr)}</span>
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ ...cell, fontWeight: 800, color: pnlColor }}>
          {pnl == null ? '—' : inrSigned(pnl)}
          {isOpen && pnl != null && <Typography component="span" sx={{ fontSize: '0.52rem', color: 'text.disabled', ml: 0.4 }}>live</Typography>}
        </TableCell>
        <TableCell sx={{ ...cell, fontWeight: 700, color: roi == null ? 'text.disabled' : roi >= 0 ? 'success.main' : 'error.main' }}>
          {roi == null ? '—' : `${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%`}
        </TableCell>
        <TableCell sx={cell}>
          {c.maxProfitInr != null || c.investedInr != null
            ? <span style={{ color: '#26a69a' }}>{c.maxProfitInr != null ? inr0(c.maxProfitInr) : '—'}</span>
            : '—'}
          {c.investedInr != null && <Typography component="span" sx={{ fontSize: '0.62rem', color: 'error.main' }}> / {inr0(c.investedInr)}</Typography>}
        </TableCell>
        <TableCell sx={cell}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Chip size="small" label={c.status} color={isOpen ? 'info' : 'default'} sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700 }} />
            {!isOpen && c.exitReason && <Typography component="span" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{reasonLabel(c.exitReason)}</Typography>}
            <Chip size="small" label={c.mode} color={c.mode === 'PAPER' ? 'warning' : 'error'} sx={{ height: 14, fontSize: '0.48rem', fontWeight: 700 }} />
          </Box>
        </TableCell>
        <TableCell sx={{ ...cell, color: 'text.secondary' }}>{durationLabel(c.entryAt, c.closedAt)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={HEADERS.length} sx={{ p: 0, borderBottom: theme => open ? `1px solid ${theme.palette.divider}` : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', mb: 0.4 }}>
                Opened {c.entryAt ? new Date(c.entryAt).toLocaleString('en-IN') : '—'}
                {c.closedAt ? ` · closed ${new Date(c.closedAt).toLocaleString('en-IN')}` : c.exitDueAt ? ` · exit due ${new Date(c.exitDueAt).toLocaleString('en-IN')}` : ''}
                {c.entrySpot ? ` · entry spot ${Math.round(Number(c.entrySpot)).toLocaleString('en-IN')}` : ''}
              </Typography>
              <Table size="small">
                <TableHead><TableRow>{['Leg', 'Side', 'Strike', 'Qty', 'Entry', 'SL', 'Exit', 'Status', 'P&L'].map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.58rem', px: 0.6, py: 0.2 }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {legs.map(l => (
                    <TableRow key={l.id}>
                      <TableCell sx={{ fontSize: '0.62rem', px: 0.6, py: 0.2 }}>{l.role}</TableCell>
                      <TableCell sx={{ fontSize: '0.62rem', px: 0.6, py: 0.2, fontWeight: 700, color: l.side === 'SELL' ? 'error.main' : 'success.main' }}>{l.side} {l.optType}</TableCell>
                      <TableCell sx={{ fontSize: '0.62rem', px: 0.6, py: 0.2 }}>{Number(l.strike).toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ fontSize: '0.62rem', px: 0.6, py: 0.2 }}>{l.qty}</TableCell>
                      <TableCell sx={{ fontSize: '0.62rem', px: 0.6, py: 0.2 }}>{l.entryPremium != null ? Number(l.entryPremium).toFixed(2) : '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.62rem', px: 0.6, py: 0.2 }}>{l.slPremium != null ? Number(l.slPremium).toFixed(2) : '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.62rem', px: 0.6, py: 0.2 }}>{l.exitPremium != null ? Number(l.exitPremium).toFixed(2) : '—'}</TableCell>
                      <TableCell sx={{ px: 0.6, py: 0.2 }}><Chip size="small" label={l.status} color={l.status === 'OPEN' ? 'info' : 'default'} sx={{ height: 14, fontSize: '0.5rem' }} /></TableCell>
                      <TableCell sx={{ fontSize: '0.62rem', px: 0.6, py: 0.2, fontWeight: 700, color: Number(l.realizedPnl) >= 0 ? 'success.main' : 'error.main' }}>{l.status === 'CLOSED' && l.realizedPnl != null ? inr0(Number(l.realizedPnl)) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CycleGrid: React.FC<{ cycles: Cycle[] }> = ({ cycles }) => {
  if (!cycles || !cycles.length) {
    return <Typography variant="body2" color="text.secondary">No trades yet — a cycle opens when the strategy's entry conditions are met (PAPER by default).</Typography>;
  }
  // Open cycles first, then most-recent closed (cycles already arrive newest-first).
  const ordered = [...cycles.filter(c => c.status === 'OPEN'), ...cycles.filter(c => c.status !== 'OPEN')];
  return (
    <Box sx={{ overflowX: 'auto', border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
      <Table size="small" sx={{ minWidth: 820 }}>
        <TableHead>
          <TableRow>{HEADERS.map((h, i) => <TableCell key={h || `c${i}`} sx={{ fontWeight: 700, fontSize: '0.6rem', px: 0.8, py: 0.5, whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>{h}</TableCell>)}</TableRow>
        </TableHead>
        <TableBody>
          {ordered.map(c => <CycleRow key={c.id} c={c} />)}
        </TableBody>
      </Table>
    </Box>
  );
};

export default CycleGrid;
